import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdPerson,
  MdSubtitles,
  MdScheduleSend,
  MdNote,
  MdExtension,
  MdPublic,
  MdHelp,
  MdAttachMoney,
  MdDelete,
  MdExitToApp,
  MdLock,
  MdWork,
  MdSupervisorAccount,
  MdCreditCard,
  MdPolicy,
} from 'react-icons/md';
import { productFeature } from 'lib/utils/productFeature';
import { useCDK } from './useCdk';
import { useAIFeatureAvailability } from './features/ai/useAIFeatureAvailability';
import { useProfileRoutes } from './useProfileRoutes';
import { useAuth } from 'lib/context/auth';
import { AccessRole } from 'lib/const/AccessRole';
/** TODO
 * All application routes must be defined in a single place.
 *
 * `useAppRoutes` is the single source of truth for route configuration
 * (path, title, icon, permissions, etc.).
 *
 * Example:
 *
 * const useAppRoutes = () => ({
 *   MY_PROFILE: {
 *     title: t('My Profile'),
 *     icon: <MdPerson size={iconSize} />,
 *     path: AppRoute.MY_PROFILE, // paths must come from enums
 *     isDisabled: false,
 *     groupBy: 'profile', -> for labels inside sibebar and menu dropdowns
 *   },
 * });
 *
 * Rules:
 * - Route paths must be defined as enums (no inline strings).
 * - Route objects must NOT be mutated.
 *
 * Role-based navigation:
 * - Sidebar / horizontal menus may define role-specific route lists
 *   (e.g. adminSidebarItems, userSidebarItems, supervisorSidebarItems).
 * - These lists must reference routes defined in `useAppRoutes`
 *   and must not redefine route metadata.
 *
 * Derived hooks such as:
 * - `useSidebarRoutes`
 * - `useHorizontalRoutes`
 *
 * should only select and order routes based on user roles or permissions.
 * They must not redefine or modify route data.
 *
 * Each user role (ADMIN, USER, SUPERVISOR, etc.) should rely on
 * `useAppRoutes` and apply permission-based filtering or selection.
 *
 * This guarantees route consistency across the app and prevents
 * navigation logic from diverging between different menus.
 */

const iconSize = 20;
const additionalIconSize = 16;

export const useUserRoutes = () => {
  const { userData, logoutUser } = useAuth();
  const { access } = userData;
  const isAdmin = access === AccessRole.ADMIN;
  const isSupervisor = access === AccessRole.SUPERVISOR;
  const { t, i18n } = useTranslation();
  const { isCdkUser } = useCDK();
  const profileRoutes = useProfileRoutes();

  const {
    isPersonalizationConfigurationAvailable,
    isAIAssistantsConfigurationAvailable,
    isAIConfigurationAvailable,
    isQuickVideosConfigurationAvailable,
  } = useAIFeatureAvailability();
  const { aiAssistants, aiConfiguration, personalization, quickVideos } =
    profileRoutes;
  const aiItems = [
    ...(isAIConfigurationAvailable ? [aiConfiguration] : []),
    ...(isAIAssistantsConfigurationAvailable ? [aiAssistants] : []),
    ...(isPersonalizationConfigurationAvailable ? [personalization] : []),
    ...(isQuickVideosConfigurationAvailable ? [quickVideos] : []),
  ];

  return useMemo(
    () => ({
      // TO_DO dobule check this do we use this in useAdminUserItems
      adminUserItems: [
        {
          title: t('My Profile'),
          icon: <MdPerson size={iconSize} />,
          path: '/profile/Profile_EditUserInfo',
          isDisabled: false,
        },
        {
          title: t('Videos'),
          isLabel: true,
        },
        {
          title: t('Teleprompter'),
          icon: <MdSubtitles size={iconSize} />,
          path: '/profile/teleprompter',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.TELEPROMPTER,
        },
        {
          title: t('Emails'),
          isLabel: true,
        },
        {
          title: t('Schedules'),
          icon: <MdScheduleSend size={iconSize} />,
          path: '/profile/schedules',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.SCHEDULES,
        },

        {
          title: t('Templates'),
          icon: <MdNote size={iconSize} />,
          path: '/profile/templates',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.TEMPLATES,
        },
        {
          title: t('Social Profiles'),
          icon: <MdPublic size={iconSize} />,
          path: '/profile/social-profiles',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.YOUTUBE,
        },
        {
          title: t('Help Center'),
          icon: <MdHelp size={iconSize} />,
          path: '/profile/help',
          productFeatureId: productFeature.HELP_CENTER,
        },
        {
          title: t('Affiliate'),
          icon: <MdAttachMoney size={iconSize} />,
          path: '/profile/affiliate',
          productFeatureId: productFeature.AFFILIATE,
        },
        {
          title: t('Recently Deleted'),
          icon: <MdDelete size={iconSize} />,
          path: '/profile/recently-deleted',
          productFeatureId: productFeature.RECENTLY_DELETED,
        },
        {
          title: t('Log Out'),
          icon: <MdExitToApp size={iconSize} />,
          path: '/Sign_Out',
          productFeatureId: productFeature.LOGOUT,
          className: 'logout',
          onItemClick: () => {
            logoutUser({ redirect: false });
          },
        },
      ],
      userItems: [
        {
          title: t('My Profile'),
          icon: <MdPerson size={iconSize} />,
          path: '/profile/Profile_EditUserInfo',
          isDisabled: false,
        },
        {
          title: t('Videos'),
          isLabel: true,
        },
        {
          title: t('Teleprompter'),
          icon: <MdSubtitles size={iconSize} />,
          path: '/profile/teleprompter',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.TELEPROMPTER,
        },
        {
          title: t('Emails'),
          isLabel: true,
        },
        {
          title: t('Templates'),
          icon: <MdNote size={iconSize} />,
          path: '/profile/templates',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.TEMPLATES,
        },
        {
          title: t('Schedules'),
          icon: <MdScheduleSend size={iconSize} />,
          path: '/profile/schedules',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.SCHEDULES,
        },
        {
          title: t('Others'),
          isLabel: true,
        },
        {
          title: t('Social Profiles'),
          icon: <MdPublic size={iconSize} />,
          path: '/profile/social-profiles',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.YOUTUBE,
        },
        {
          title: t('Help Center'),
          icon: <MdHelp size={iconSize} />,
          path: '/profile/help',
          productFeatureId: productFeature.HELP_CENTER,
        },
        {
          title: t('Affiliate'),
          icon: <MdAttachMoney size={iconSize} />,
          path: '/profile/affiliate',
          productFeatureId: productFeature.AFFILIATE,
        },
        {
          title: t('Recently Deleted'),
          icon: <MdDelete size={iconSize} />,
          path: '/profile/recently-deleted',
          productFeatureId: productFeature.RECENTLY_DELETED,
        },
        ...(isCdkUser
          ? [
              {
                title: t('Terms and Conditions'),
                icon: <MdPolicy size={20} />,
                path: '/profile/terms-and-conditions',
                isDisabled: false,
              },
            ]
          : []),
        {
          title: t('Log Out'),
          icon: <MdExitToApp size={iconSize} />,
          path: '/Sign_Out',
          productFeatureId: productFeature.LOGOUT,
          className: 'logout',
          onItemClick: () => {
            logoutUser({ redirect: false });
          },
        },
      ],

      supervisorItems: [
        {
          title: t('My Profile'),
          icon: <MdPerson size={iconSize} />,
          path: '/profile/Profile_EditUserInfo',
          isDisabled: false,
        },
        {
          title: t('Company Profile'),
          icon: <MdWork size={20} />,
          path: `/profile/Profile_EditCompanyInfo`,
          isDisabled: false,
        },
        {
          title: t('Manage Users'),
          icon: <MdSupervisorAccount size={20} />,
          path: `/users`,
          isDisabled: false,
        },

        {
          title: t('Billing'),
          icon: <MdCreditCard size={20} />,
          path: '/billing',
          isDisabled: false,
        },
        {
          title: t('Add-ons'),
          icon: <MdExtension size={20} />,
          path: `/profile/add-ons`,
          isDisabled: false,
          fontWeight: 600,
        },
        ...(isAdmin || isSupervisor ? aiItems : []),
        {
          title: t('Emails'),
          isLabel: true,
        },
        {
          title: t('Templates'),
          icon: <MdNote size={iconSize} />,
          path: '/profile/templates',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.TEMPLATES,
        },
        {
          title: t('Schedules'),
          icon: <MdScheduleSend size={iconSize} />,
          path: '/profile/schedules',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.SCHEDULES,
        },
        {
          title: t('Others'),
          isLabel: true,
        },
        {
          title: t('Social Profiles'),
          icon: <MdPublic size={iconSize} />,
          path: '/profile/social-profiles',
          additionalIcon: (
            <MdLock size={additionalIconSize} color={'#001B53'} />
          ),
          productFeatureId: productFeature.YOUTUBE,
        },

        {
          title: t('Help Center'),
          icon: <MdHelp size={iconSize} />,
          path: '/profile/help',
          productFeatureId: productFeature.HELP_CENTER,
        },
        {
          title: t('Affiliate'),
          icon: <MdAttachMoney size={iconSize} />,
          path: '/profile/affiliate',
          productFeatureId: productFeature.AFFILIATE,
        },
        {
          title: t('Recently Deleted'),
          icon: <MdDelete size={iconSize} />,
          path: '/profile/recently-deleted',
          productFeatureId: productFeature.RECENTLY_DELETED,
        },
        ...(isCdkUser
          ? [
              {
                title: t('Terms and Conditions'),
                icon: <MdPolicy size={20} />,
                path: '/profile/terms-and-conditions',
                isDisabled: false,
              },
            ]
          : []),
        {
          title: t('Log Out'),
          icon: <MdExitToApp size={iconSize} />,
          path: '/Sign_Out',
          productFeatureId: productFeature.LOGOUT,
          className: 'logout',
          onItemClick: () => {
            logoutUser({ redirect: false });
          },
        },
      ],
    }),
    [i18n.language]
  );
};
